import DiscountService from "@/store/services/discount.service";
import PlanService from "@/store/services/plan.service"
import GekkoProductService from "@/store/services/gekkoproduct.service"
import BillingSettingService from "@/store/services/billingsetting.service"

export default {
  namespaced: true,
  state: {
    products: [],
    activePlan: null,
    upcomingPlan: null,
    upaidPlan: null,
    minTeamSize: 0,
    teamSize: 0,
    availableTiers: [],
    tier: null,
    currentCycle: null,
    activeDiscount: [],
    billingSettings: null,
    paymentDetails: null,
    nextPayment: null,
    nextTier: null
  },
  getters: {
    getDiscount: state => state.activeDiscount,
    getProducts: state => state.products,
    getProductByType: state => type => state.products.find(item => item.type === type),
    getProductByTierId: state => tierId => state.products.filter(item => item.type === 'accountants').find(item => item.tier.id === tierId),

    getActivePlan: state => state.activePlan,
    getIsFreePremium: state => {
      if (state.activePlan) {
        let free_plan_line = state.activePlan.planline_set.find(line => line.is_free === true);
        if (free_plan_line) {
          return true;
        }
      }
      return false;
    },

    getMinTeamSize: state => state.minTeamSize,
    getTeamSize: state => state.teamSize,

    getAvailableTiers: state => state.availableTiers,
    getTier: state => state.tier,

    getBillingSettings: state => state.billingSettings,
    getPaymentDetails: state => state.paymentDetails,

    getNextPayment: state => state.nextPayment,
    getNextTier: state => state.nextTier,

    getCurrentTier: (state) => {
      let planlines = state.activePlan?.planline_set || []
      return planlines[0]?.product?.tier
    }
  },
  mutations: {
    SET_ACTIVE_PLAN (state, plan) {
      state.activePlan = plan
    },
    SET_MIN_TEAM_SIZE (state, size) {
      state.minTeamSize = size
    },
    SET_TEAM_SIZE (state, size) {
      state.teamSize = size
    },
    SET_AVAILABLE_TIERS (state, tiers) {
      state.availableTiers = tiers
    },
    SET_TIER (state, tier) {
      state.tier = tier
    },
    SET_ACTIVE_DISCOUNT (state, discount) {
      state.activeDiscount = discount
    },
    SET_PRODUCTS (state, products) {
      state.products = products
    },
    SET_BILLING_SETTINGS (state, settings) {
      state.billingSettings = settings
    },
    SET_PAYMENT_DETAILS (state, data) {
      state.paymentDetails = data
    },
    SET_NEXT_PAYMENT (state, data) {
      state.nextPayment = data
    },
    SET_NEXT_TIER (state, data) {
      state.nextTier = data
    }
  },
  actions: {
    loadSubscriptionsMainData ({ dispatch }) {
      return Promise.all([
        dispatch('loadProducts'),
        dispatch('loadBillingSettings').then(() => {
          return dispatch('loadDiscounts');
        }),
        dispatch('loadPaymentMethods'),
        dispatch('loadPlans'),
        dispatch('loadTiers')
      ])
    },
    loadProducts ({ commit }) {
      return GekkoProductService.list().then((response) => {
        for(var i=0;i<response.data.length;i++){
          response.data[i].cost = Number(response.data[i].cost);
        }
        commit('SET_PRODUCTS', response.data)
      })
    },
    loadNextPayment ({ commit, getters }, pk) {
      let params = {
        type: "premium"
      }
      if (getters.getActivePlan.planline_set.length > 0 && getters.getActivePlan.planline_set[0].product?.type === 'accountants') {
        params.type = "accountants"
        params.tier = getters.getActivePlan.planline_set[0].product
      }
      return PlanService.getNextPayment(pk, params).then((response) => {
        if (response.status === 204) return
        commit('SET_NEXT_PAYMENT', response.data)
        if (params.type === "accountants") {
          PlanService.getUpcomingTier(pk).then((response) => {
            commit('SET_NEXT_TIER', response.data)
          })
        }
      })
    },
    loadBillingSettings ({ commit }) {
      return BillingSettingService.retrieveCurrent().then((response) => {
        commit('SET_BILLING_SETTINGS', response.data)
      })
    },
    loadPaymentMethods ({ commit }) {
      return BillingSettingService.getPaymentMethods().then((response) => {
        commit('SET_PAYMENT_DETAILS', response.data)
      })
    },
    loadPlans ({ commit, dispatch, getters }) {
      return PlanService.list({ extended: true }).then((response) => {

        const planList = response.data
        let team_size = 0
        planList.forEach((plan) => {
          switch (plan.status) {
            case 'active':
              commit('SET_ACTIVE_PLAN', plan)
              team_size = plan.team_size
              break;
            case 'grace':
              if (plan.grace_date) {
                // We keep this date comparison for safety, but the backend should never send a grace plan without a grace date, and the grace date should always be in the future
                const today = new Date()
                const graceDate = new Date(plan.grace_date)
                if (graceDate >= today) {
                  commit('SET_ACTIVE_PLAN', plan)
                  team_size = plan.team_size
                }
              }
              break;
          }
        })

        commit('SET_TEAM_SIZE', team_size)
        commit('SET_MIN_TEAM_SIZE', team_size)
        if (getters.getActivePlan) {
          dispatch('loadNextPayment', getters.getActivePlan.id)
        }
      })
    },
    loadTiers({commit}) {
      BillingSettingService.getAvailableTiers().then((response) => {
        commit('SET_AVAILABLE_TIERS', response.data)
      })
    },
    loadDiscounts ({ commit }) {
      return DiscountService.list().then((response) => {
        let subscriptionDiscount
        if (response.data.length > 0) {
          // the highest priority will always be the first element in the array
          subscriptionDiscount = response.data[0]
        } else {
          // FALLBACK if not subscription discounts are found, hardcode the yearly subscription
          subscriptionDiscount = {
            amount: '15.00',
            short_line_description: this.getters.getRoot.$gettext('Yearly discount (15%)')
          }
        }
        
        let discountTriggerAmount
        if (subscriptionDiscount.benefit_type === 'amount') {
          discountTriggerAmount = '€' + subscriptionDiscount.amount
        } else {
          discountTriggerAmount = parseInt(subscriptionDiscount.amount) + '%'
        }
        subscriptionDiscount.triggerText = discountTriggerAmount + ' ' + this.getters.getRoot.$gettext('off!')
        subscriptionDiscount.triggerAmount = discountTriggerAmount
        
        commit('SET_ACTIVE_DISCOUNT', subscriptionDiscount)
      })
    },
    updateSubscription ({ state, commit }, { router, payload }) {
      const { yearlyCycle, updatedCycle, uncancelTier, teamMembers, updatedTeamSize, tier, updatedTier } = payload
      const update_promises = []
      
      if (updatedTier || uncancelTier) {
        update_promises.push(
          () => PlanService.changeTier({ tier: tier }).then((response) => {
            if (response.status === 204) { // downgrade
              if(uncancelTier) {
                commit('setAlert', {
                  title: this.getters.getRoot.$gettext('Subscription reactivated'),
                  text: this.getters.getRoot.$gettext('Your subscription has been successfully reactivated.'),
                  variant: 'success',
                  duration: 4
                }, { root: true })
              } else if(!tier) {
                commit('setAlert', {
                  title: this.getters.getRoot.$gettext('Subscription downgraded'),
                  text: this.getters.getRoot.$gettext('Your subscription has been successfully downgraded. You can continue using the current plan until the end of the billing cycle.'),
                  variant: 'warning',
                  duration: 4
                }, { root: true })
              }
            } else { // upgrade
              console.log('upgrade')

              const url = router.resolve({
                name: 'ConfirmPayment',
                params: { transactionId: response.data }
              }).href
              window.location.assign(url)
            }
          })
        )
      }
      
      if (updatedCycle) {
          update_promises.push(
            () => BillingSettingService.changeRecurrency(state.billingSettings.id, { renewal_months: (yearlyCycle) ? 12 : 1 })
          )
      }

      if (updatedTeamSize) {
        update_promises.push(
          () => PlanService.addTeamMember({ team_members: teamMembers }).then((response) => {
            router.push({ name: 'ConfirmPayment', params: { transactionId: response.data } })
          })
        )
      }

      return update_promises.reduce((previousPromise, nextPromise) => {
          return previousPromise.then(() => {
              return nextPromise()
          })
      }, Promise.resolve())
    }
  }
};